import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { AlertMessages } from '../AlertMessages';
import { getStockReport } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { FormContainer } from './styles';

export const StockReport = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Carregar filial do localStorage, ou usar ALTO_BRANCO como padrão apenas na primeira vez
  const [branch, setBranch] = useState(() => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (savedBranch && (savedBranch === 'ALTO_BRANCO' || savedBranch === 'CONCEICAO')) {
        return savedBranch;
      }
    } catch (error) {
      console.error('[StockReport] Erro ao carregar filial do localStorage:', error);
    }
    const defaultBranch = 'ALTO_BRANCO';
    // Salvar padrão no localStorage na primeira vez
    try {
      localStorage.setItem('canteen_filial', defaultBranch);
    } catch (error) {
      console.error('[StockReport] Erro ao salvar filial padrão:', error);
    }
    return defaultBranch;
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  // Garantir que a filial padrão seja salva no localStorage na primeira vez e sincronizar estado
  useEffect(() => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (!savedBranch || (savedBranch !== 'ALTO_BRANCO' && savedBranch !== 'CONCEICAO')) {
        const defaultBranch = branch || 'ALTO_BRANCO';
        localStorage.setItem('canteen_filial', defaultBranch);
        if (branch !== defaultBranch) {
          setBranch(defaultBranch);
        }
      } else if (savedBranch !== branch) {
        setBranch(savedBranch);
      }
    } catch (error) {
      console.error('[StockReport] Erro ao salvar filial padrão no localStorage:', error);
    }
  }, []); // Apenas na montagem

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleGenerateReport = async () => {
    if (startDate && endDate && startDate > endDate) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Data início deve ser anterior à data fim',
      });
      return;
    }

    try {
      setLoading(true);
      const filters = {};
      if (startDate) filters.startDate = startDate.format('YYYY-MM-DD');
      if (endDate) filters.endDate = endDate.format('YYYY-MM-DD');
      if (branch) filters.branch = branch;
      
      const data = await getStockReport(filters);
      setReport(data);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormContainer>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0b4f6c' }}>
            Relatório de Entradas de Estoque
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                  label="Data Início (opcional)"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                  label="Data Fim (opcional)"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="branch-select-label">Filial</InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  value={branch}
                  label="Filial"
                  onChange={(e) => {
                    const newBranch = e.target.value;
                    if (newBranch === 'ALTO_BRANCO' || newBranch === 'CONCEICAO') {
                      setBranch(newBranch);
                      // Salvar no localStorage
                      try {
                        localStorage.setItem('canteen_filial', newBranch);
                      } catch (error) {
                        console.error('[StockReport] Erro ao salvar filial no localStorage:', error);
                      }
                    }
                  }}
                >
                  <MenuItem value="ALTO_BRANCO">Alto Branco</MenuItem>
                  <MenuItem value="CONCEICAO">Conceição</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerateReport}
                disabled={loading}
                sx={{
                  bgcolor: '#1e958c',
                  padding: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0b4f6c' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Gerar Relatório'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {report && (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Período
                </Typography>
                <Typography>
                  <strong>Início:</strong> {report.period?.startDate ? formatDate(report.period.startDate) : 'Todas as entradas'}
                </Typography>
                <Typography>
                  <strong>Fim:</strong> {report.period?.endDate ? formatDate(report.period.endDate) : 'Todas as entradas'}
                </Typography>
              </CardContent>
            </Card>

            {report.report && report.report.length > 0 ? (
              <Box>
                {report.report.map((item, index) => (
                  <Accordion key={item.product?.id || index} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2, alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {item.product?.name || `Produto #${item.product?.id}`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                            Entradas: {item.totalEntries || 0} | Saídas: {item.totalExits || 0}
                          </Typography>
                          <Typography 
                            sx={{ 
                              color: (item.availableStock || 0) > 0 ? '#1e958c' : '#d32f2f', 
                              fontWeight: 600, 
                              ml: 2 
                            }}
                          >
                            Estoque: {item.availableStock || 0} unidades
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell align="right">Quantidade</TableCell>
                              <TableCell align="center">Registrado por</TableCell>
                              <TableCell>Data/Hora</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {item.entries.map((entry) => (
                              <TableRow key={entry.id}>
                                <TableCell>{formatDate(entry.entryDate)}</TableCell>
                                <TableCell align="right">{entry.quantity}</TableCell>
                                <TableCell align="center">
                                  {entry.creator?.name ? (
                                    <Tooltip title={entry.creator.name} arrow>
                                      <IconButton size="small" sx={{ color: '#1e958c' }}>
                                        <InfoOutlined fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" color="textSecondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', padding: 4, color: '#666' }}>
                Nenhuma entrada encontrada para o período selecionado
              </Box>
            )}
          </Box>
        )}

        {!report && !loading && (
          <Box sx={{ textAlign: 'center', padding: 4, color: '#666' }}>
            Selecione um período e clique em "Gerar Relatório" para visualizar as entradas de estoque
          </Box>
        )}
      </FormContainer>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
